import { useState } from "react";
import { api } from "@/lib/api";

/**
 * Modal "📚 Leer guion → Crear personajes faltantes".
 *
 * Flujo:
 *   1. Click "📚 Leer guion" → llama a `/detect-missing` (sin IA, análisis de texto)
 *   2. Si hay N nombres faltantes, abre un wizard "1 de N"
 *   3. Por cada nombre: usuario elige categoría (4 radios) o pulsa "Saltar"
 *   4. Al final, crea TODAS las fichas en lote con POST /characters
 *
 * Vive fuera del editor protegido.
 */
const CATEGORIES = [
  { id: "protagonista", label: "Protagonista", color: "#941620" },
  { id: "secundario",   label: "Secundario",   color: "#26775E" },
  { id: "esporadico",   label: "Secundario esporádico", color: "#A87C26" },
  { id: "figuracion",   label: "Figuración con frase", color: "#6B7280" },
];

export default function DetectCharactersModal({ open, onClose, projectId, onApplied }) {
  // phases: idle (scan button) → scanning → wizard (cycling through missing) → applying → done
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [missing, setMissing] = useState([]);   // [{name, count}]
  const [currentIdx, setCurrentIdx] = useState(0);
  const [decisions, setDecisions] = useState({}); // { "JOAN": "secundario" | "skip" }

  const reset = () => {
    setPhase("idle");
    setError("");
    setMissing([]);
    setCurrentIdx(0);
    setDecisions({});
  };

  const scan = async () => {
    setPhase("scanning");
    setError("");
    try {
      const r = await api.get(`/icaa/projects/${projectId}/screenplay/characters/detect-missing`);
      const list = r.data?.missing || [];
      setMissing(list);
      if (list.length === 0) {
        setPhase("done");
      } else {
        setCurrentIdx(0);
        setPhase("wizard");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al leer el guion";
      setError(String(msg));
      setPhase("idle");
    }
  };

  const decideCurrent = (decision) => {
    const current = missing[currentIdx];
    if (!current) return;
    setDecisions((p) => ({ ...p, [current.name]: decision }));
    if (currentIdx < missing.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      // Final paso
      applyAll({ ...decisions, [current.name]: decision });
    }
  };

  const goBack = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  const applyAll = async (finalDecisions) => {
    setPhase("applying");
    const toCreate = missing.filter((m) => {
      const d = finalDecisions[m.name];
      return d && d !== "skip";
    });
    if (toCreate.length === 0) {
      setPhase("done");
      onApplied?.([]);
      return;
    }
    try {
      const created = [];
      for (const m of toCreate) {
        const cat = finalDecisions[m.name];
        const r = await api.post(`/icaa/projects/${projectId}/screenplay/characters`, {
          name: m.name.toUpperCase(),
          category: cat,
        });
        created.push(r.data);
      }
      onApplied?.(created);
      setPhase("done");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al crear fichas";
      setError(String(msg));
      setPhase("wizard");
    }
  };

  if (!open) return null;

  const current = missing[currentIdx];
  const createdCount = Object.values(decisions).filter((v) => v && v !== "skip").length;
  const skippedCount = Object.values(decisions).filter((v) => v === "skip").length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => { if (phase !== "scanning" && phase !== "applying") { reset(); onClose?.(); } }}
      data-testid="detect-characters-modal"
    >
      <div
        className="relative flex w-[640px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              Leer guion · Crear personajes
            </p>
            <p className="mt-0.5 font-serif text-base text-stone-900">
              Detecta nombres del guion que aún no tienen ficha
            </p>
          </div>
          <button
            onClick={() => { if (phase !== "scanning" && phase !== "applying") { reset(); onClose?.(); } }}
            data-testid="detect-chars-close"
            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-[#941620]"
          >
            ✕ Cerrar
          </button>
        </header>

        {phase === "idle" && (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="text-[3rem]">📚</div>
            <h3 className="mt-3 font-serif text-2xl text-stone-900">Lee el guion y completa la matriz</h3>
            <p className="mt-3 max-w-md text-[13px] leading-relaxed text-stone-600">
              Escanea todas las líneas PERSONAJE y propone crear fichas para los nombres que aún no tienen una.
              Te preguntará la categoría de cada uno (Protagonista / Secundario / Esporádico / Figuración).
            </p>
            {error && (
              <p className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">{error}</p>
            )}
            <button
              onClick={scan}
              data-testid="detect-chars-scan"
              className="mt-6 rounded-full bg-[#941620] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019]"
            >
              Leer guion ahora
            </button>
          </div>
        )}

        {(phase === "scanning" || phase === "applying") && (
          <div className="flex flex-col items-center justify-center p-10">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#941620]" />
            <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-stone-500">
              {phase === "scanning" ? "Leyendo guion…" : "Creando fichas…"}
            </p>
          </div>
        )}

        {phase === "wizard" && current && (
          <div className="space-y-4 p-5" data-testid="detect-chars-wizard">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-stone-500">
              <span>Personaje {currentIdx + 1} de {missing.length}</span>
              <span>
                <span className="text-emerald-700">{createdCount} a crear</span>
                {skippedCount > 0 && <span className="ml-3 text-stone-500">· {skippedCount} saltado{skippedCount === 1 ? "" : "s"}</span>}
              </span>
            </div>

            <div className="rounded-xl border-2 border-stone-200 bg-white px-5 py-6 text-center">
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Detectado en el guion</p>
              <p className="mt-2 font-mono text-3xl font-bold uppercase tracking-wider text-stone-900" data-testid="detect-chars-name">
                {current.name}
              </p>
              <p className="mt-2 text-[12px] text-stone-500">
                Aparece <strong>{current.count}</strong> {current.count === 1 ? "vez" : "veces"} en líneas PERSONAJE
              </p>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                ¿A qué categoría asignarlo?
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => decideCurrent(cat.id)}
                    data-testid={`detect-chars-pick-${cat.id}`}
                    className="flex items-center gap-2 rounded-lg border-2 border-stone-200 bg-white px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-wider text-stone-800 transition hover:border-current"
                    style={{ color: cat.color }}
                  >
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: cat.color }} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">{error}</p>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <button
                onClick={goBack}
                disabled={currentIdx === 0}
                className="rounded-full border border-stone-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 transition hover:border-stone-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                ← Atrás
              </button>
              <button
                onClick={() => decideCurrent("skip")}
                data-testid="detect-chars-skip"
                className="rounded-full border border-stone-400 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 transition hover:border-stone-700"
              >
                Saltar este →
              </button>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="text-[2.5rem]">✓</div>
            <h3 className="mt-2 font-serif text-xl text-stone-900">
              {createdCount > 0
                ? `${createdCount} ficha${createdCount === 1 ? "" : "s"} creada${createdCount === 1 ? "" : "s"}`
                : "Sin fichas que crear"}
            </h3>
            <p className="mt-2 max-w-md text-[12px] text-stone-600">
              {missing.length === 0
                ? "Todos los personajes del guion ya tienen ficha."
                : `${skippedCount} saltado${skippedCount === 1 ? "" : "s"}. Las fichas creadas aparecerán en el panel de Personajes con campos vacíos. Usa el botón "✨ Sugerir contenido" para que la IA los rellene a partir del guion.`}
            </p>
            <button
              onClick={() => { reset(); onClose?.(); }}
              className="mt-5 rounded-full bg-[#26775E] px-5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1d5a47]"
            >
              Listo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
